import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import * as d3 from 'd3';
import { finalize, Subject, takeUntil } from 'rxjs';
import { ReportsService } from '../../reports.service';
import { InbuiltUtilityService } from '../inbuilt-utility.service';
import { DecimalPipe } from '@angular/common';

interface SpeedometerSegment {
  from: number;
  to: number;
  color: string;
}

@Component({
    selector: 'app-inbuilt-speedometer-v2',
    templateUrl: './inbuilt-speedometer-v2.component.html',
    styleUrl: './inbuilt-speedometer-v2.component.css',
    imports: [DecimalPipe]
})
export class InbuiltSpeedometerV2Component implements OnInit, AfterViewInit, OnDestroy {
  @Input() config: any;
  @Input() elementData: any = null;
  @ViewChild('speedometerContainer', { static: true }) private speedometerContainer!: ElementRef<HTMLElement>;
  @Input() Temp: boolean = false;
  minValue: number = 0;
  maxValue: number = 372000000;
  currentValue: number = 151800000;
  ticks: number[] = [0, 124000000, 248000000, 372000000];
  segments: SpeedometerSegment[] = [
    { from: 0, to: 111600000, color: '#c7463f' },
    { from: 111600000, to: 260400000, color: '#efb46b' },
    { from: 260400000, to: 372000000, color: '#78c89f' }
  ];
  private resizeObserver: ResizeObserver | null = null;
  private readonly destroy$ = new Subject<void>();
  private selectedFilters: any = {};
  private inputSearchFilter: string = '';
  private lastSelectedFiltersSignature = '';
  private valueFormat: 'currency' | 'count' = 'currency';
  loading = false;

  constructor(
    private reportsService: ReportsService,
    private inbuiltUtilityService: InbuiltUtilityService
  ) {}

  ngOnInit(): void {
    this.selectedFilters = this.config?.selectedFilters || {};
    this.lastSelectedFiltersSignature = JSON.stringify({ filters: this.selectedFilters, search: this.inputSearchFilter });
    this.subscribeToFilterEvents();
    this.callSpeedometerApi();
  }

  ngAfterViewInit(): void {
    this.renderSpeedometer();
    this.resizeObserver = new ResizeObserver(() => {
      this.renderSpeedometer();
    });
    this.resizeObserver.observe(this.speedometerContainer.nativeElement);
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToFilterEvents(): void {
    this.inbuiltUtilityService.getSelectedFilterEvents()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        if (!this.shouldApplyFilterEvent(event)) {
          return;
        }

        const nextFilters = event?.selectedFilters || {};
        const nextInputSearchFilter = event?.inputSearchFilter || '';
        const nextSignature = JSON.stringify({ filters: nextFilters, search: nextInputSearchFilter });
        if (nextSignature === this.lastSelectedFiltersSignature) {
          return;
        }

        this.selectedFilters = nextFilters;
        this.inputSearchFilter = nextInputSearchFilter;
        this.lastSelectedFiltersSignature = nextSignature;
        this.callSpeedometerApi();
      });
  }

  private shouldApplyFilterEvent(event: any): boolean {
    if (!event || !this.config) {
      return false;
    }

    const eventReport = String(event?.reportNameFromQueryParams ?? event?.report ?? '');
    const eventPage = String(event?.currentPage?.key ?? event?.page ?? '');
    const eventTab = String(event?.currentTab?.key ?? event?.tab ?? '');
    const myReport = String(this.config?.reportNameFromQueryParams ?? '');
    const myPage = String(this.config?.currentPage?.key ?? '');
    const myTab = String(this.config?.currentTab?.key ?? '');

    if (eventReport && eventReport !== myReport) {
      return false;
    }
    if (eventPage && eventPage !== myPage) {
      return false;
    }
    if (eventTab && eventTab !== myTab) {
      return false;
    }

    return true;
  }

  private callSpeedometerApi(): void {
    this.loading = true;

    let payload = {
      report: this.config?.reportNameFromQueryParams,
      page: this.config?.currentPage ? this.config.currentPage.key : '',
      tab: this.config?.currentTab ? this.config.currentTab.key : '',
      elementName: this.elementData?.name || '',
      elementType: this.elementData?.type || 'speedometer',
      download: false,
      selectedFilters: this.inbuiltUtilityService.getTablePayload(this.selectedFilters),
      inputSearchFilter: this.inputSearchFilter
    };
    if (!this.Temp) {
      payload = this.inbuiltUtilityService.getTablePayload(this.selectedFilters);
    }
    this.reportsService.getInbuildTableData(payload, this.Temp)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (response: any) => {
          this.bindFromResponse(response);
        },
        error: (error: any) => {
          console.error('Speedometer API error:', error);
        }
      });
  }

  private bindFromResponse(response: any): void {
    const rows: any[] = Array.isArray(response?.data) ? response.data : [];
    const columnInfo: any = response?.columnInfo || {};

    const targetKey = this.findKey(rows, columnInfo, 'totaltarget');
    const benefitKey = this.findKey(rows, columnInfo, 'totalbenefit');

    if (!targetKey) {
      return;
    }

    const rawMax = this.sumKey(rows, targetKey);
    const rawCurrent = benefitKey ? this.sumKey(rows, benefitKey) : 0;

    // Determine display format from columnInfo
    const metricType = String(columnInfo[targetKey] ?? '').trim().toUpperCase();
    this.valueFormat = metricType === 'COUNT' ? 'count' : 'currency';

    this.minValue = 0;
    this.maxValue = rawMax > 0 ? rawMax : 1;
    this.currentValue = rawCurrent;

    // Ticks: [0, q1(25%), q2(50%), q3(75%), max]
    const q2 = this.maxValue / 2;
    const q1 = q2 / 2;
    const q3 = (q2 + this.maxValue) / 2;
    this.ticks = [this.minValue, q1, q2, q3, this.maxValue];

    // Segments: 0-30% danger, 30-70% warning, 70-100% success
    const seg1End = this.maxValue * 0.3;
    const seg2End = this.maxValue * 0.7;
    this.segments = [
      { from: this.minValue, to: seg1End, color: '#c7463f' },
      { from: seg1End, to: seg2End, color: '#efb46b' },
      { from: seg2End, to: this.maxValue, color: '#78c89f' }
    ];

    this.ticks = this.getSanitizedTicks();
    this.segments = this.getSanitizedSegments();

    this.renderSpeedometer();
  }

  private findKey(rows: any[], columnInfo: any, normalizedTarget: string): string | null {
    const colKey = Object.keys(columnInfo).find(
      (k: string) => this.normalizeKey(k) === normalizedTarget
    );
    if (colKey) {
      return colKey;
    }
    const sample = rows[0] || {};
    const rowKey = Object.keys(sample).find(
      (k: string) => this.normalizeKey(k) === normalizedTarget
    );
    return rowKey || null;
  }

  private normalizeKey(key: string): string {
    return String(key ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  private sumKey(rows: any[], key: string): number {
    return rows.reduce((acc: number, row: any) => acc + this.toNumber(row?.[key], 0), 0);
  }

  formatValue(value: number): string {
    return this.valueFormat === 'count' ? this.formatCount(value) : this.formatCompact(value);
  }

  private formatCount(value: number): string {
    if (!Number.isFinite(value)) {
      return '0';
    }
    return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }

  get currentPercentage(): number {
    if (this.maxValue <= this.minValue) {
      return 0;
    }
    return ((this.currentValue - this.minValue) / (this.maxValue - this.minValue)) * 100;
  }

  formatCompact(value: number): string {
    const sign = value < 0 ? '-' : '';
    const absValue = Math.abs(value);

    if (absValue >= 1000000000) {
      return `${sign}$${this.formatScaled(absValue / 1000000000)}B`;
    }
    if (absValue >= 1000000) {
      return `${sign}$${this.formatScaled(absValue / 1000000)}M`;
    }
    if (absValue >= 1000) {
      return `${sign}$${this.formatScaled(absValue / 1000)}K`;
    }

    return `${sign}$${this.formatScaled(absValue)}`;
  }

  private formatScaled(value: number): string {
    const rounded = Number(value.toFixed(1));
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  }

  private toNumber(value: any, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }

  private getSanitizedTicks(): number[] {
    const allTicks = [this.minValue, ...this.ticks, this.maxValue]
      .map((tick: number) => this.clamp(tick, this.minValue, this.maxValue));

    return [...new Set(allTicks)].sort((a: number, b: number) => a - b);
  }

  private getSanitizedSegments(): SpeedometerSegment[] {
    const normalized = this.segments
      .map((segment: SpeedometerSegment) => ({
        from: this.clamp(segment.from, this.minValue, this.maxValue),
        to: this.clamp(segment.to, this.minValue, this.maxValue),
        color: this.normalizeColor(segment.color)
      }))
      .filter((segment: SpeedometerSegment) => segment.to > segment.from)
      .sort((a: SpeedometerSegment, b: SpeedometerSegment) => a.from - b.from);

    if (normalized.length === 0) {
      return [{ from: this.minValue, to: this.maxValue, color: '#9ca3af' }];
    }

    return normalized;
  }

  private normalizeColor(value: any): string {
    const normalized = String(value ?? '')
      .trim()
      .replace(/^color\s*:\s*/i, '');

    return normalized || '#9ca3af';
  }

  private getRenderedSegments(): SpeedometerSegment[] {
    const sorted = [...this.segments]
      .map((segment: SpeedometerSegment) => ({
        from: this.clamp(segment.from, this.minValue, this.maxValue),
        to: this.clamp(segment.to, this.minValue, this.maxValue),
        color: this.normalizeColor(segment.color)
      }))
      .filter((segment: SpeedometerSegment) => segment.to > segment.from)
      .sort((a: SpeedometerSegment, b: SpeedometerSegment) => a.from - b.from);

    if (!sorted.length) {
      return [{ from: this.minValue, to: this.maxValue, color: '#78c89f' }];
    }

    const output: SpeedometerSegment[] = [];
    let cursor = this.minValue;

    sorted.forEach((segment: SpeedometerSegment) => {
      if (segment.to <= cursor) {
        return;
      }

      const from = Math.max(segment.from, cursor);
      if (from > cursor) {
        output.push({
          from: cursor,
          to: from,
          color: output.length ? output[output.length - 1].color : sorted[0].color
        });
      }

      output.push({ from, to: segment.to, color: segment.color });
      cursor = segment.to;
    });

    if (cursor < this.maxValue) {
      output.push({ from: cursor, to: this.maxValue, color: output[output.length - 1].color });
    }

    return output;
  }

  private renderSpeedometer(): void {
    const host = this.speedometerContainer?.nativeElement;
    if (!host) {
      return;
    }

    d3.select(host).selectAll('*').remove();

    const width = Math.max(18, host.clientWidth || 18);
    const centerX = width / 2;
    const radius = width * 0.40;
    const labelPad = width * 0.09;
    const topPad = width * 0.03;
    const bottomPad = width * 0.12;
    const centerY = topPad + labelPad + radius;
    const height = centerY + bottomPad;
    const arcThickness = radius * 0.26;

    // D3 arc angles use 0 at 12 o'clock. Use -PI/2 to PI/2 for a left-to-right top semicircle.
    const startAngle = -Math.PI / 2;
    const endAngle = Math.PI / 2;

    const valueScale = d3
      .scaleLinear()
      .domain([this.minValue, this.maxValue])
      .range([startAngle, endAngle]);

    const svg = d3
      .select(host)
      .append('svg')
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('width', '100%')
      .style('height', 'auto');

    const chart = svg.append('g').attr('transform', `translate(${centerX}, ${centerY})`);

    const baseArc = d3
      .arc<any>()
      .innerRadius(radius - arcThickness)
      .outerRadius(radius)
      .startAngle(startAngle)
      .endAngle(endAngle);

    chart
      .append('path')
      .attr('d', baseArc as any)
      .attr('fill', '#374151');

    const renderedSegments = this.getRenderedSegments();

    renderedSegments.forEach((segment: SpeedometerSegment, index: number) => {
      const anglePadding = 0.0015;
      const rawStart = valueScale(segment.from);
      const rawEnd = valueScale(segment.to);
      const start = index === 0 ? startAngle : Math.max(startAngle, rawStart - anglePadding);
      const end = index === renderedSegments.length - 1 ? endAngle : Math.min(endAngle, rawEnd + anglePadding);

      const segmentArc = d3
        .arc<any>()
        .innerRadius(radius - arcThickness)
        .outerRadius(radius)
        .startAngle(start)
        .endAngle(end);

      chart
        .append('path')
        .attr('d', segmentArc as any)
        .attr('fill', segment.color)
        .attr('stroke', segment.color)
        .attr('stroke-width', radius * 0.002);
    });

    const tickGroup = chart.append('g').attr('class', 'tick-group');
    const tickLength = radius * 0.08;

    this.ticks.forEach((tickValue: number) => {
      const angle = valueScale(tickValue);
      const xDir = Math.sin(angle);
      const yDir = -Math.cos(angle);

      const labelRadius = radius + tickLength * 2.35;
      const isMinTick = Math.abs(tickValue - this.minValue) < 0.0001;
      const isMaxTick = Math.abs(tickValue - this.maxValue) < 0.0001;
      const isEndTick = isMinTick || isMaxTick;
      // drop end labels below the arc endpoints without pushing them outside the viewBox
      const endVerticalPad = isEndTick ? radius * 0.10 : 0;
      const minLabelOffset = radius * 0.03;
      const maxLabelOffset = radius * 0.02;
      const labelX = (labelRadius * xDir) + (isMinTick ? minLabelOffset : isMaxTick ? maxLabelOffset : 0);
      const labelY = (labelRadius * yDir) + endVerticalPad;
      const labelAnchor = isMinTick ? 'start' : isMaxTick ? 'end' : 'middle';

      tickGroup
        .append('text')
        .attr('x', labelX)
        .attr('y', labelY)
        .attr('text-anchor', labelAnchor)
        .attr('dominant-baseline', 'middle')
        .style('font-size', `0.150rem`)
        .style('fill', '#111827')
        .text(this.formatValue(tickValue));
    });

    const needleGroup = chart.append('g').attr('class', 'needle-group');

    const hubRadiusOuter = radius * 0.14;
    const hubRadiusInner = radius * 0.095;
    const semiCircleArc = d3
      .arc<any>()
      .innerRadius(hubRadiusInner)
      .outerRadius(hubRadiusOuter)
      .startAngle(-Math.PI / 2)
      .endAngle(Math.PI / 2);

    needleGroup
      .append('path')
      .attr('d', semiCircleArc as any)
      .attr('fill', '#3f3f46');

    const needleLength = radius * 0.84;
    const needleBaseWidth = radius * 0.055;
    const needleBaseX = radius * 0.07;

    needleGroup
      .append('polygon')
      .attr('points', `${needleLength},0 ${needleBaseX},${-needleBaseWidth} ${needleBaseX},${needleBaseWidth}`)
      .attr('fill', '#ffffff')
      .attr('stroke', '#6b7280')
      .attr('stroke-width', radius * 0.008)
      .attr('stroke-linejoin', 'round');

    needleGroup
      .append('circle')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', radius * 0.028)
      .attr('fill', '#4b5563');

    const clampedValue = this.clamp(this.currentValue, this.minValue, this.maxValue);
    const valueRatio = (clampedValue - this.minValue) / (this.maxValue - this.minValue);
    const targetAngleDegrees = -180 + (180 * valueRatio);

    needleGroup
      .transition()
      .duration(1500)
      .ease(d3.easeCubicOut)
      .attrTween('transform', () => {
        const interpolator = d3.interpolateNumber(-180, targetAngleDegrees);
        return (time: number) => `rotate(${interpolator(time)})`;
      });
  }
}
